import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaFileExport } from "react-icons/fa";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

interface ShippingOrder {
  id: string;
  shippingNumber: string;
  outboundOrderNo?: string;
  status: string;
  createdAt: string;
  customerName?: string;
  warehouseName?: string;
  totalQty?: number;
  totalPicked?: number;
}

const ShippingOrdersList = () => {
  const [orders, setOrders] = useState<ShippingOrder[]>([]);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<keyof ShippingOrder>("shippingNumber");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const { logout } = useAuth();

  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL;

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return {
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
      },
    };
  };

  const sortFieldMap: Record<string, string> = {
    shippingNumber: "shipping_number",
    outboundOrderNo: "outbound_order_no",
    customerName: "customer_name",
    warehouseName: "warehouse_name",
    createdAt: "created_at",
    status: "status",
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const backendSortField = sortFieldMap[sortField] || "created_at";
      const res = await axios.get(`${API}/shipping-orders`, {
        ...getAuthHeader(),
        params: {
          page: currentPage,
          limit: rowsPerPage,
          search,
          sortField: backendSortField,
          sortOrder: sortOrder.toUpperCase(),
        },
      });

      setOrders(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (error: any) {
      console.error("Failed to fetch shipping orders", error);
      if (error.response?.status === 401) {
        logout();
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (search) setCurrentPage(1);
      fetchOrders();
    }, 300);

    return () => clearTimeout(timeout);
  }, [search, currentPage, rowsPerPage, sortField, sortOrder]);

  const handleSort = (field: keyof ShippingOrder) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
  };

  const handleExport = () => {
    const ws = XLSX.utils.json_to_sheet(orders || []);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ShippingOrders");
    const blob = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([blob]), "shipping_orders.xlsx");
  };

  const maxPage = Math.ceil(total / rowsPerPage);
  const currentData = useMemo(() => orders || [], [orders]);

  return (
    <div className="p-1 mx-auto max-w-6xl">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-lg font-semibold text-gray-800">Shipping Orders</h1>
          <button
            className="bg-blue-600 text-white text-sm px-3 py-1 rounded hover:bg-blue-700"
            onClick={() => navigate("/shipping-orders/create")}
          >
            + Add Shipping Order
          </button>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-2">
          <div className="relative w-full md:w-1/3">
            <input
              type="text"
              placeholder="Search..."
              className="border px-2 py-1 rounded text-sm w-full pr-8"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                &#10005;
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <select
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
              className="border rounded px-2 py-1 text-sm"
            >
              {[10, 25, 50, 100].map((num) => (
                <option key={num} value={num}>{num} / page</option>
              ))}
            </select>
            |
            <button
              onClick={handleExport}
              className="text-blue-600 hover:text-blue-800 text-lg"
              title="Export to Excel"
            >
              <FaFileExport />
            </button>
          </div>
        </div>

        <div className="overflow-auto">
          <table className="w-full text-sm table-auto border border-gray-300">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="border px-2 py-1 w-20">#</th>
                <th
                  className="border px-2 py-1 cursor-pointer"
                  onClick={() => handleSort("shippingNumber")}
                >
                  Shipping No {sortField === "shippingNumber" && (sortOrder === "asc" ? "▲" : "▼")}
                </th>
                <th
                  className="border px-2 py-1 cursor-pointer"
                  onClick={() => handleSort("outboundOrderNo")}
                >
                  Outbound Ord No {sortField === "outboundOrderNo" && (sortOrder === "asc" ? "▲" : "▼")}
                </th>
                <th
                  className="border px-2 py-1 cursor-pointer"
                  onClick={() => handleSort("customerName")}
                >
                  Customer {sortField === "customerName" && (sortOrder === "asc" ? "▲" : "▼")}
                </th>
                <th
                  className="border px-2 py-1 cursor-pointer"
                  onClick={() => handleSort("warehouseName")}
                >
                  Warehouse {sortField === "warehouseName" && (sortOrder === "asc" ? "▲" : "▼")}
                </th>
                <th
                  className="border px-2 py-1 cursor-pointer"
                  onClick={() => handleSort("status")}
                >
                  Status {sortField === "status" && (sortOrder === "asc" ? "▲" : "▼")}
                </th>
                <th
                  className="border px-2 py-1 cursor-pointer"
                  onClick={() => handleSort("createdAt")}
                >
                  Created At {sortField === "createdAt" && (sortOrder === "asc" ? "▲" : "▼")}
                </th>
                <th className="border px-2 py-1 w-10 text-center">Actions</th>
              </tr>
            </thead>

            <tbody>
              {currentData.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-4">No shipping orders found.</td>
                </tr>
              ) : (
                currentData.map((order, index) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="border px-2 py-1 text-center">
                      {(currentPage - 1) * rowsPerPage + index + 1}
                    </td>
                    <td className="border px-2 py-1">{order.shippingNumber}</td>
                    <td className="border px-2 py-1">{order.outboundOrderNo || "-"}</td>
                    <td className="border px-2 py-1">{order.customerName || "-"}</td>
                    <td className="border px-2 py-1">{order.warehouseName || "-"}</td>
                    <td className="border px-2 py-1">{order.status}</td>
                    <td className="border px-2 py-1">{new Date(order.createdAt).toLocaleDateString()}</td>
                    <td className="border px-2 py-1 text-center">
                      <button
                        className="text-blue-600 hover:text-blue-800 mr-2"
                        title="Edit"
                        onClick={() => navigate(`/shipping-orders/edit/${order.id}`)}
                      >
                        <FaEdit />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-between items-center mt-4 text-sm flex-wrap gap-2">
          <div>
            Showing {(currentPage - 1) * rowsPerPage + 1}–{Math.min(currentPage * rowsPerPage, total)} of {total}
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <button className="px-2 py-1 border rounded disabled:opacity-30" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
              <ChevronsLeft size={18} />
            </button>
            <button className="px-2 py-1 border rounded disabled:opacity-30" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1}>
              «
            </button>
            {Array.from({ length: maxPage }, (_, i) => i + 1)
              .filter((page) => Math.abs(page - currentPage) <= 2)
              .map((page) => (
                <button
                  key={page}
                  className={`px-2 py-1 border rounded ${page === currentPage ? "bg-blue-600 text-white" : "bg-white text-gray-700 hover:bg-gray-100"}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
            <button className="px-2 py-1 border rounded disabled:opacity-30" onClick={() => setCurrentPage(prev => Math.min(prev + 1, maxPage))} disabled={currentPage === maxPage}>
              »
            </button>
            <button className="px-2 py-1 border rounded disabled:opacity-30" onClick={() => setCurrentPage(maxPage)} disabled={currentPage === maxPage}>
              <ChevronsRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShippingOrdersList;
